import {
  StyledElipsisMultiTextDiv,
  StyledElipsisTextSpan,
  StyledTooltipTrigger,
} from "./Tooltip.styles";
import { Tooltip } from "../../molecules/Tooltip";
import { useRef, useState } from "react";

/**
 * Ellipsis
 *
 * A wrapper component that truncates overflowing content with an ellipsis
 * and shows a tooltip on hover if the content is clipped.
 *
 * Params
 * - as (React.ElementType): Component or element to render as the wrapper.
 *   Defaults to `StyledElipsisTextSpan`.
 * - children (React.ReactNode): The content to display inside the wrapper.
 * - text (string): The text to show inside the tooltip. If not provided, `children` are used.
 * - tooltipClassName (string): Additional class name(s) to apply to the Tooltip.
 * - ...props (object): Any other props are forwarded to the wrapper component
 *   (e.g. `style`, `onClick`, `data-*` attributes).
 *
 * Returns
 * - JSX.Element: The truncating wrapper and, when hovered and clipped,
 *   a positioned Tooltip.
 */
const Ellipsis = ({
  as: Wrapper = StyledElipsisTextSpan,
  children,
  shouldShowPopover = false,
  text = "",
  tooltipClassName = "",
  ...props
}) => {
  const ref = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const maybeShow = () => {
    const el = ref.current;
    if (el && (el.scrollWidth > el.clientWidth || shouldShowPopover)) setAnchor(el);
  };

  return (
    <>
      <Wrapper {...props} ref={ref} onMouseEnter={maybeShow} onMouseLeave={() => setAnchor(null)}>
        {children}
      </Wrapper>
      {anchor && <Tooltip anchorEl={anchor} className={tooltipClassName} text={text || children} />}
    </>
  );
};

/**
 * EllipsisText
 *
 * A specialized `Ellipsis` that uses a styled `<span>` for single-line text truncation.
 *
 * Params
 * - all Ellipsis params (see `Ellipsis`).
 *
 * Returns
 * - JSX.Element: An `Ellipsis` configured to render as a `<span>`.
 */
export const EllipsisText = props => <Ellipsis as={StyledElipsisTextSpan} {...props} />;

/**
 * EllipsisMultiText
 *
 * A specialized `Ellipsis` that uses a styled `<div>` for multi-line text truncation,
 * applying a `"limit"` tooltip class by default.
 *
 * Params
 * - all Ellipsis params (see `Ellipsis`).
 *
 * Returns
 * - JSX.Element: An `Ellipsis` configured to render as a `<div>` with multi-line support.
 */
export const EllipsisMultiText = props => (
  <Ellipsis as={StyledElipsisMultiTextDiv} tooltipClassName="limit" {...props} />
);

/**
 * CustomTooltip
 *
 * A generic tooltip component that shows the tooltip on hover.
 */
export const CustomTooltip = ({ children, text, ...props }) => (
  <Ellipsis as={StyledTooltipTrigger} shouldShowPopover={true} text={text} {...props}>
    {children}
  </Ellipsis>
);
